"use client";

import { useState, useEffect, useRef } from "react";
import { usePathname, useRouter } from "next/navigation";
import { useTranslations } from "next-intl";
import { Sidebar } from "./sidebar";
import { MobileHeader } from "./mobile-header";
import { MobileNavigation } from "./mobile-navigation";
import { DevToolbar } from "./dev-toolbar";
import { DegradationBanner } from "./billing/degradation-banner";
import { useAuthStore } from "@/stores/auth-store";
import { getAuthToken, setOnUnauthorized } from "@/lib/api/http-client";

/**
 * Auth / public full-bleed pages: no sidebar, and **no session guard**.
 *
 * Halaman yang tidak terdaftar di sini akan didorong ke `/login` oleh tiga
 * guard di bawah begitu tidak ada token lokal. Untuk `/masuk` itu fatal:
 * penukaran token berjalan asinkron, jadi redirect memenangi balapan
 * sementara penukaran tetap berhasil di belakang — token sekali-pakai
 * terbakar tanpa menghasilkan sesi, dan pelanggan tidak bisa mencoba lagi.
 *
 * Dikunci `__tests__/auth-pages-contract.test.ts`.
 */
export const authPages = [
  "/",
  "/login",
  "/signup",
  "/forgot-password",
  "/check-email",
  "/reset-password",
  "/password-reset-success",
  "/accept-invite",
  // SUBSC-20 — halaman handoff auto-login. Lihat catatan di atas.
  "/masuk",
];

export function DashboardShell({ children }: { children: React.ReactNode }) {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const pathname = usePathname();
  const router = useRouter();
  const t = useTranslations("nav");
  const { isAuthenticated, user, checkAuth } = useAuthStore();
  const hasCheckedAuthRef = useRef(false);

  const isAuthPage = authPages.some((p) => pathname === p || pathname.startsWith(p + "/"));
  /** Iframe-friendly routes: no sidebar, header, or dev toolbar (still requires auth). */
  const isEmbedPage = pathname.startsWith("/embed");

  // Register global 401 handler: clear session and redirect to login
  useEffect(() => {
    setOnUnauthorized(() => {
      useAuthStore.getState().logout();
      router.push("/login");
    });
    return () => setOnUnauthorized(null);
  }, [router]);

  // On protected pages: validate token once on load (handles rehydration + expired token)
  useEffect(() => {
    if (isAuthPage) return;
    const token = getAuthToken();
    if (!token) {
      router.push("/login");
      return;
    }
    if (hasCheckedAuthRef.current) return;
    hasCheckedAuthRef.current = true;
    checkAuth();
  }, [isAuthPage, router, checkAuth]);

  // Redirect to login if not authenticated (after checkAuth or when no token)
  useEffect(() => {
    if (!isAuthPage && !isAuthenticated && !getAuthToken()) {
      router.push("/login");
    }
  }, [isAuthPage, isAuthenticated, router]);

  // Auth pages render without sidebar
  if (isAuthPage) {
    return <div className="min-h-screen bg-gray-50">{children}</div>;
  }

  // Not authenticated - show nothing while redirecting
  if (!isAuthenticated || !user) {
    return <div className="min-h-screen bg-gray-50" />;
  }

  if (isEmbedPage) {
    return <div className="h-screen min-h-[100dvh] overflow-hidden bg-gray-50">{children}</div>;
  }

  const pageTitles: Record<string, string> = {
    "/dashboard": t("dashboard"),
    "/inbox": t("inbox"),
    "/campaigns": t("campaigns"),
    "/analytics": t("analytics"),
    "/customers": t("customers"),
    "/ai-chatbot": t("aiChatbot"),
    "/intent-management": t("intentManagement"),
    "/automation": t("automation"),
    "/segment-builder": t("segmentBuilder"),
    "/support-tickets": t("supportTickets"),
    "/live-chat-widget": t("liveChatWidget"),
    "/livechat": t("livechat"),
    "/billing": t("billing"),
    "/settings": t("settings"),
    "/profile-settings": t("profileSettings"),
    "/help": t("help"),
    "/templates": t("templates"),
    "/audit-log": t("auditLog"),
  };

  const pageTitle = pageTitles[pathname] || "Kustomer Suite";
  const showSearch = pathname === "/inbox" || pathname === "/customers";

  return (
    <div className="h-screen overflow-hidden bg-gray-50">
      {/* Mobile Header */}
      <MobileHeader onMenuClick={() => setIsMobileSidebarOpen(true)} title={pageTitle} showSearch={showSearch} />

      {/* Sidebar */}
      <Sidebar
        isCollapsed={isSidebarCollapsed}
        onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
        isMobileOpen={isMobileSidebarOpen}
        onMobileClose={() => setIsMobileSidebarOpen(false)}
      />

      {/* Main Content */}
      <div
        className={`transition-all duration-300 h-full overflow-hidden min-w-0 flex flex-col
          ${isSidebarCollapsed ? "md:ml-20" : "md:ml-64"}
          pt-14 md:pt-0
          pb-16 md:pb-0
        `}
      >
        {/* QC-F11 — satu tempat untuk seluruh halaman terautentikasi, supaya
            tahap degradasi tidak hanya terlihat di /billing. shrink-0 + anak
            di bawahnya min-h-0 supaya halaman yang mengandalkan h-full (mis.
            inbox) tetap menyusut mengikuti ruang yang tersisa, bukan meluber. */}
        <DegradationBanner />
        <div className="flex-1 min-h-0 overflow-hidden">{children}</div>
      </div>

      {/* Mobile Bottom Navigation */}
      <MobileNavigation />

      {/* Dev Toolbar (development only) */}
      <DevToolbar />
    </div>
  );
}
